import { clamp, type Rect, type Size } from "../../common/geometry.js";

/**
 * The ways one face of a `FlipCard` can give way to the other.
 *
 * Every one of these is built from what Webwin's renderer actually has: a
 * translate, a *uniform* scale, an axis-aligned clip and a flat scrim. There
 * is no arbitrary rotation in `RenderContext` — its transform is a translate
 * plus one scale factor, deliberately, because that is what keeps every clip a
 * scissor rectangle — so nothing here spins a face about its center. A turn is
 * a squash instead, which is what a rotation about an edge looks like anyway,
 * and `flipX` / `flipY` approximate that squash with strips (see `turn`).
 */
export type FlipTransition =
  /** Cut, with no animation at all. */
  | "none"
  /** Dissolve through the card's own ground color and back out. */
  | "fade"
  /** Turn about the vertical axis, as a playing card is turned over. */
  | "flipX"
  /** Turn about the horizontal axis. */
  | "flipY"
  /** Both faces travel together, the new one following the old one out. */
  | "slideLeft"
  | "slideRight"
  | "slideUp"
  | "slideDown"
  /** The new face slides in over a face that stays where it is. */
  | "coverLeft"
  | "coverRight"
  | "coverUp"
  | "coverDown"
  /** The old face slides away, uncovering a new one that was already there. */
  | "revealLeft"
  | "revealRight"
  | "revealUp"
  | "revealDown"
  /** Through the viewer: the old face grows past, the new one arrives small. */
  | "zoomIn"
  /** Away from the viewer: the old face recedes, the new one drops back in. */
  | "zoomOut"
  /** The old face parts in the middle and draws back like curtains. */
  | "curtainX"
  | "curtainY"
  /** A row of shutters, each turning a beat after the one before it. */
  | "blindsX"
  | "blindsY";

/** Every concrete transition, in the order the demo and the designer list them. */
export const FLIP_TRANSITIONS: readonly FlipTransition[] = [
  "none",
  "fade",
  "flipX",
  "flipY",
  "slideLeft",
  "slideRight",
  "slideUp",
  "slideDown",
  "coverLeft",
  "coverRight",
  "coverUp",
  "coverDown",
  "revealLeft",
  "revealRight",
  "revealUp",
  "revealDown",
  "zoomIn",
  "zoomOut",
  "curtainX",
  "curtainY",
  "blindsX",
  "blindsY",
];

/**
 * What a card is asked for: a transition, or the word "random".
 *
 * Resolved once when a turn starts rather than per frame — a transition that
 * changed its mind halfway would not be an animation.
 */
export type FlipTransitionSpec = FlipTransition | "random";

/** Which of the two faces a part belongs to. */
export type FlipFace = "from" | "to";

/**
 * One rectangle of one face, as it is to be painted this frame.
 *
 * A part is deliberately not a whole face: a curtain shows two pieces of the
 * same face traveling opposite ways, and a blind shows six. Coordinates are
 * in the card's *content* space, whose origin is the top-left of the area the
 * faces fill.
 */
export interface FlipPart {
  face: FlipFace;
  /** Offset from the face's laid-out position. */
  dx: number;
  dy: number;
  /** Uniform scale about the center of the content area. */
  scale: number;
  /** The window this part is seen through. */
  clip: Rect;
  /** Darkening painted over the part, 0..1. Depth, not color. */
  shade: number;
  /** Blend toward the card's ground color, 0..1. What a dissolve is made of. */
  veil: number;
}

/** The knobs a turn's shape is built from; a card passes its own. */
export interface FlipShape {
  /**
   * How many strips a turn is cut into.
   *
   * A strip is translated, not squashed — there is no non-uniform scale to
   * squash it with — so this is the *sampling rate* of the squash, and each
   * strip shows only the middle of the source strip it stands for. Too few and
   * a line of text comes apart into recognizable chunks; enough and the same
   * dropped pixels read as a squash. Sixteen is where that crossover sits for
   * body text. It is the cost knob as well: every strip is another pass over
   * the face's contents, so a turn costs `strips` times an ordinary frame for
   * the third of a second it runs.
   */
  strips: number;
  /**
   * How many shutters a blind is made of.
   *
   * Separate from `strips`, which shares none of its reasoning: a shutter is
   * something to look at rather than an approximation of anything, and the
   * count that reads as a venetian blind is not the count that resamples a
   * face convincingly.
   */
  shutters: number;
  /** How far a face shrinks at the moment it is edge-on, 0..1. */
  perspective: number;
  /** Darkening at that same moment, 0..1. */
  shade: number;
}

export const DEFAULT_FLIP_SHAPE: FlipShape = {
  strips: 16,
  shutters: 7,
  perspective: 0.12,
  shade: 0.5,
};

/** Reverse the sense of a transition, so coming back retraces going out. */
export function mirrorTransition(transition: FlipTransition): FlipTransition {
  switch (transition) {
    case "slideLeft":
      return "slideRight";
    case "slideRight":
      return "slideLeft";
    case "slideUp":
      return "slideDown";
    case "slideDown":
      return "slideUp";
    case "coverLeft":
      return "coverRight";
    case "coverRight":
      return "coverLeft";
    case "coverUp":
      return "coverDown";
    case "coverDown":
      return "coverUp";
    case "revealLeft":
      return "revealRight";
    case "revealRight":
      return "revealLeft";
    case "revealUp":
      return "revealDown";
    case "revealDown":
      return "revealUp";
    case "zoomIn":
      return "zoomOut";
    case "zoomOut":
      return "zoomIn";
    // A turn, a dissolve and a blind are symmetric: they look the same run
    // either way, so there is nothing to mirror, and inventing a difference
    // would make "reverse on the way back" a lie in half the cases.
    default:
      return transition;
  }
}

/**
 * Settle a spec into a transition. `random` picks from everything that has an
 * animation, which is every transition but `none`.
 */
export function pickTransition(
  spec: FlipTransitionSpec,
  random: () => number = Math.random,
): FlipTransition {
  if (spec !== "random") return spec;
  const animated = FLIP_TRANSITIONS.filter((t) => t !== "none");
  const index = clamp(Math.floor(random() * animated.length), 0, animated.length - 1);
  return animated[index]!;
}

/**
 * Ease a turn's progress.
 *
 * `start` is how much of it was already done when it began — what is left of a
 * turn that was reversed part-way. A turn of its own smooths both ends; one
 * picked up in flight is already moving, so it only eases out. Easing into a
 * face that was visibly traveling is a stall the eye catches. The same rule,
 * and the same reason, as `Carousel`'s slide.
 */
export function easeFlip(t: number, start = 0): number {
  const clamped = clamp(t, 0, 1);
  if (start > 0) return start + (1 - start) * (1 - (1 - clamped) ** 2);
  return clamped < 0.5 ? 2 * clamped * clamped : 1 - (-2 * clamped + 2) ** 2 / 2;
}

const wholeRect = (size: Size): Rect => ({ x: 0, y: 0, width: size.width, height: size.height });

function part(face: FlipFace, size: Size, over: Partial<FlipPart> = {}): FlipPart {
  return { face, dx: 0, dy: 0, scale: 1, clip: wholeRect(size), shade: 0, veil: 0, ...over };
}

/**
 * A face part-way through a turn, cut into strips.
 *
 * `c` is what is left of the face along the turning axis: 1 face-on, 0
 * edge-on. The whole face is drawn once per strip, each time offset so that
 * strip lands where the squash would have put it, and clipped to that slot.
 * The strips are drawn at the *perspective* scale rather than at `c`, so each
 * one always covers at least the slot it is cut to and the seams can never
 * open into gaps — the clip trims the overlap instead.
 */
function turn(
  face: FlipFace,
  c: number,
  size: Size,
  shape: FlipShape,
  axis: "x" | "y",
): FlipPart[] {
  const strips = Math.max(1, Math.round(shape.strips));
  const scale = 1 - (1 - c) * shape.perspective;
  const shade = (1 - c) * shape.shade;
  const along = axis === "x" ? size.width : size.height;
  const center = along / 2;
  const parts: FlipPart[] = [];

  for (let i = 0; i < strips; i++) {
    const from = (along * i) / strips;
    const to = (along * (i + 1)) / strips;
    const mid = (from + to) / 2;
    // Where this strip's center has to end up, less where the uniform scale
    // would otherwise have put it.
    const shift = (mid - center) * (c - scale);
    const slotStart = center + (from - center) * c;
    const slotSize = (to - from) * c;
    parts.push({
      face,
      dx: axis === "x" ? shift : 0,
      dy: axis === "x" ? 0 : shift,
      scale,
      clip:
        axis === "x"
          ? { x: slotStart, y: 0, width: slotSize, height: size.height }
          : { x: 0, y: slotStart, width: size.width, height: slotSize },
      shade,
      veil: 0,
    });
  }
  return parts;
}

/** A row of shutters, each running the same turn a beat after its neighbor. */
function blinds(t: number, size: Size, shape: FlipShape, axis: "x" | "y"): FlipPart[] {
  const count = Math.max(2, Math.round(shape.shutters));
  // How much of the run is spent staggering; the rest is one shutter's turn.
  const spread = 0.4;
  const span = 1 - spread;
  const along = axis === "x" ? size.width : size.height;
  const parts: FlipPart[] = [];

  for (let i = 0; i < count; i++) {
    const begins = (spread * i) / (count - 1);
    const local = clamp((t - begins) / span, 0, 1);
    const c = Math.abs(Math.cos(Math.PI * local));
    const from = (along * i) / count;
    const to = (along * (i + 1)) / count;
    const mid = (from + to) / 2;
    const half = ((to - from) * c) / 2;
    parts.push({
      face: local < 0.5 ? "from" : "to",
      dx: 0,
      dy: 0,
      scale: 1,
      clip:
        axis === "x"
          ? { x: mid - half, y: 0, width: half * 2, height: size.height }
          : { x: 0, y: mid - half, width: size.width, height: half * 2 },
      // Softer than a whole-card turn: a shutter is small, and the darkening
      // is there to tell it from its neighbors rather than to sell a rotation.
      shade: (1 - c) * shape.shade * 0.6,
      veil: 0,
    });
  }
  return parts;
}

/**
 * The parts to paint at eased progress `t`, in paint order.
 *
 * The order carries what would otherwise need a z field: a `cover` puts the
 * arriving face last so it lands on top, a `reveal` puts the departing one
 * last so it is the thing sliding away, and a turn or a dissolve shows only
 * one face at a time so the question never arises.
 */
export function flipFrame(
  transition: FlipTransition,
  t: number,
  size: Size,
  shape: FlipShape = DEFAULT_FLIP_SHAPE,
): FlipPart[] {
  const p = clamp(t, 0, 1);
  const w = size.width;
  const h = size.height;
  /** How far a face merely being passed over is pushed back. */
  const under = 0.3;

  switch (transition) {
    case "none":
      return [part(p < 0.5 ? "from" : "to", size)];

    case "fade":
      return p < 0.5
        ? [part("from", size, { veil: p * 2 })]
        : [part("to", size, { veil: (1 - p) * 2 })];

    case "flipX":
    case "flipY": {
      const axis = transition === "flipX" ? "x" : "y";
      const c = Math.abs(Math.cos(Math.PI * p));
      return turn(p < 0.5 ? "from" : "to", c, size, shape, axis);
    }

    case "slideLeft":
      return [part("from", size, { dx: -w * p }), part("to", size, { dx: w * (1 - p) })];
    case "slideRight":
      return [part("from", size, { dx: w * p }), part("to", size, { dx: -w * (1 - p) })];
    case "slideUp":
      return [part("from", size, { dy: -h * p }), part("to", size, { dy: h * (1 - p) })];
    case "slideDown":
      return [part("from", size, { dy: h * p }), part("to", size, { dy: -h * (1 - p) })];

    case "coverLeft":
      return [part("from", size, { shade: p * under }), part("to", size, { dx: w * (1 - p) })];
    case "coverRight":
      return [part("from", size, { shade: p * under }), part("to", size, { dx: -w * (1 - p) })];
    case "coverUp":
      return [part("from", size, { shade: p * under }), part("to", size, { dy: h * (1 - p) })];
    case "coverDown":
      return [part("from", size, { shade: p * under }), part("to", size, { dy: -h * (1 - p) })];

    case "revealLeft":
      return [part("to", size, { shade: (1 - p) * under }), part("from", size, { dx: -w * p })];
    case "revealRight":
      return [part("to", size, { shade: (1 - p) * under }), part("from", size, { dx: w * p })];
    case "revealUp":
      return [part("to", size, { shade: (1 - p) * under }), part("from", size, { dy: -h * p })];
    case "revealDown":
      return [part("to", size, { shade: (1 - p) * under }), part("from", size, { dy: h * p })];

    case "zoomIn":
      return p < 0.5
        ? [part("from", size, { scale: 1 + 0.2 * (p * 2), veil: p * 2 })]
        : [part("to", size, { scale: 0.8 + 0.2 * ((p - 0.5) * 2), veil: (1 - p) * 2 })];
    case "zoomOut":
      return p < 0.5
        ? [part("from", size, { scale: 1 - 0.2 * (p * 2), veil: p * 2 })]
        : [part("to", size, { scale: 1.2 - 0.2 * ((p - 0.5) * 2), veil: (1 - p) * 2 })];

    case "curtainX": {
      const half = w / 2;
      return [
        part("to", size),
        part("from", size, {
          dx: -half * p,
          clip: { x: 0, y: 0, width: half * (1 - p), height: h },
        }),
        part("from", size, {
          dx: half * p,
          clip: { x: half + half * p, y: 0, width: half * (1 - p), height: h },
        }),
      ];
    }
    case "curtainY": {
      const half = h / 2;
      return [
        part("to", size),
        part("from", size, {
          dy: -half * p,
          clip: { x: 0, y: 0, width: w, height: half * (1 - p) },
        }),
        part("from", size, {
          dy: half * p,
          clip: { x: 0, y: half + half * p, width: w, height: half * (1 - p) },
        }),
      ];
    }

    case "blindsX":
      return blinds(p, size, shape, "x");
    case "blindsY":
      return blinds(p, size, shape, "y");
  }
}
